import type { LiftType, ShelvingCellConfig, WardrobeModule } from "../types";

export type { LiftType };

/**
 * Широкие фасады ячеек стеллажа и антресоли (09.10). Распашная дверца шире
 * 600 мм тяжела для петель, поэтому вместо неё — две дверцы или один
 * горизонтальный фасад, который открывается вверх на подъёмнике Blum
 * Aventos (HL, HS, складной HF, поворотный HK top) или откидывается вниз.
 *
 * HL и HS поднимают фасад выше корпуса (10.10): над фасадом нужно свободное
 * место — верхний ряд стеллажа или антресоль с запасом до потолка. В средних
 * рядах — HF или HK: они открываются перед корпусом.
 */
export const SINGLE_HINGED_FRONT_MAX_WIDTH_MM = 600;

export type FlapDirection = "up" | "down";
/** Как открывается фасад ячейки: распашная (петли слева/справа), две дверцы, вверх, вниз. */
export type CellFrontOpening = "left" | "right" | "double" | "up" | "down";

export interface LiftSpec {
  type: LiftType;
  title: string;
  hint: string;
  /** Высота фасада, при которой подъёмник работает, мм (по каталогу Blum — высота корпуса). */
  minMm: number;
  maxMm: number;
  /** Сколько места нужно над фасадом, мм: HL поднимает его целиком, HS кладёт сверху на корпус. */
  headroomMm: (heightMm: number) => number;
}

/** Высоты по каталогу Blum: HK top 205–600, HL 300–580, HS 350–800, HF 480–1040 мм. */
export const LIFT_SPECS: readonly LiftSpec[] = [
  { type: "HL", title: "Aventos HL", hint: "фасад поднимается параллельно вверх над корпусом", minMm: 300, maxMm: 580,
    headroomMm: (heightMm) => Math.round(heightMm) + 20 },
  { type: "HS", title: "Aventos HS", hint: "фасад поднимается и ложится над корпусом", minMm: 350, maxMm: 800,
    headroomMm: () => 100 },
  { type: "HF", title: "Aventos HF", hint: "складной: фасад из двух частей складывается вверх", minMm: 480, maxMm: 1040,
    headroomMm: () => 0 },
  { type: "HK", title: "Aventos HK top", hint: "фасад поворачивается вверх и стоит в любом положении", minMm: 205, maxMm: 600,
    headroomMm: () => 0 }
];
export const liftSpec = (type: LiftType) => LIFT_SPECS.find((spec) => spec.type === type)!;
export const isLiftType = (value: unknown): value is LiftType => LIFT_SPECS.some((spec) => spec.type === value);
export const LIFT_MIN_HEIGHT_MM = Math.min(...LIFT_SPECS.map((spec) => spec.minMm));
export const LIFT_MAX_HEIGHT_MM = Math.max(...LIFT_SPECS.map((spec) => spec.maxMm));

/** Откидной вниз: механизм с доводчиком держит фасад до этой высоты. */
export const FLAP_DOWN_MIN_HEIGHT_MM = 150;
export const FLAP_DOWN_MAX_HEIGHT_MM = 600;
/** Шов между частями складного фасада и между двумя дверцами, мм. */
export const FRONT_SPLIT_GAP_MM = 2;

/**
 * Почему подъёмник не подходит: высота фасада вне диапазона или над фасадом
 * мало места (headroomMm — свободно над верхней кромкой, 0 — сверху ячейка).
 */
export const liftUnavailableReason = (type: LiftType, heightMm: number, headroomMm = Infinity,
  /** Подъёмники, которые у этого изделия не ставят (у комода — HS, 15.10). */
  excluded: readonly LiftType[] = []): string | null => {
  const spec = liftSpec(type);
  if (excluded.includes(type)) return `${spec.title} у комодов не ставим`;
  if (heightMm < spec.minMm || heightMm > spec.maxMm) {
    return `${spec.title} — для фасадов высотой ${spec.minMm}–${spec.maxMm} мм, здесь ${Math.round(heightMm)} мм`;
  }
  const need = spec.headroomMm(heightMm);
  if (need > 0 && headroomMm < need) {
    return headroomMm <= 0
      ? `${spec.title} поднимает фасад выше корпуса, а сверху ячейка или антресоль — только для верхнего ряда`
      : `${spec.title}: над корпусом нужно ${need} мм, до потолка ${Math.round(headroomMm)} мм`;
  }
  return null;
};

export const liftTypesFor = (heightMm: number, headroomMm = Infinity, excluded: readonly LiftType[] = []) =>
  LIFT_SPECS.filter((spec) => !liftUnavailableReason(spec.type, heightMm, headroomMm, excluded)).map((spec) => spec.type);

/** Выбранный подъёмник, если подходит по высоте и месту сверху; иначе — первый подходящий. */
export const resolvedLiftType = (chosen: LiftType | undefined, heightMm: number, headroomMm = Infinity,
  excluded: readonly LiftType[] = []): LiftType | undefined => {
  const fitting = liftTypesFor(heightMm, headroomMm, excluded);
  return chosen && fitting.includes(chosen) ? chosen : fitting[0];
};

export const flapUpProblem = (heightMm: number, headroomMm = Infinity, excluded: readonly LiftType[] = []) =>
  liftTypesFor(heightMm, headroomMm, excluded).length
  ? null
  : `Подъёмники Aventos — для фасадов высотой ${LIFT_MIN_HEIGHT_MM}–${LIFT_MAX_HEIGHT_MM} мм, здесь ${Math.round(heightMm)} мм`;

export const flapDownProblem = (heightMm: number) =>
  heightMm < FLAP_DOWN_MIN_HEIGHT_MM || heightMm > FLAP_DOWN_MAX_HEIGHT_MM
    ? `Откидной вниз — для фасадов высотой ${FLAP_DOWN_MIN_HEIGHT_MM}–${FLAP_DOWN_MAX_HEIGHT_MM} мм, здесь ${Math.round(heightMm)} мм`
    : null;

export const flapProblem = (direction: FlapDirection, heightMm: number, headroomMm = Infinity, excluded: readonly LiftType[] = []) =>
  direction === "up" ? flapUpProblem(heightMm, headroomMm, excluded) : flapDownProblem(heightMm);

/** Распашная одна дверца — до 600 мм; шире — две дверцы или откидной фасад. */
export const singleDoorProblem = (widthMm: number) => widthMm > SINGLE_HINGED_FRONT_MAX_WIDTH_MM + 0.5
  ? `Одна дверца шире ${SINGLE_HINGED_FRONT_MAX_WIDTH_MM} мм тяжела для петель — две дверцы или откидной фасад`
  : null;

export interface FlapFront {
  direction: FlapDirection;
  /** Тип подъёмника для «вверх». */
  lift?: LiftType;
  /** Высоты частей снизу вверх: у складного HF — две, у остальных — одна. */
  partHeightsMm: number[];
}

/** Фасад вверх или вниз заданной высоты; null — если такой механизм не подходит. */
export const flapFront = (
  direction: FlapDirection,
  chosenLift: LiftType | undefined,
  heightMm: number,
  headroomMm = Infinity,
  excluded: readonly LiftType[] = []
): FlapFront | null => {
  if (flapProblem(direction, heightMm, headroomMm, excluded)) return null;
  if (direction === "down") return { direction, partHeightsMm: [heightMm] };
  const lift = resolvedLiftType(chosenLift, heightMm, headroomMm, excluded)!;
  const partHeightsMm = lift === "HF"
    ? [(heightMm - FRONT_SPLIT_GAP_MM) / 2, (heightMm - FRONT_SPLIT_GAP_MM) / 2]
    : [heightMm];
  return { direction, lift, partHeightsMm };
};

/** Петли на откидном фасаде: у HF — между частями, у откидного вниз — внизу; HL, HS и HK петель не требуют. */
export const flapHingeCount = (front: FlapFront, widthMm: number) =>
  front.direction === "up" && front.lift !== "HF" ? 0 : widthMm > 900 ? 3 : 2;

export const flapMechanismName = (front: FlapFront) => front.direction === "up"
  ? `Подъёмник ${liftSpec(front.lift!).title} — ${liftSpec(front.lift!).hint}`
  : "Откидной механизм вниз с доводчиком";

/** Как открывается — для описания визуализации. */
export const flapMotionEnglish = (front: FlapFront) => front.direction === "down"
  ? "horizontal drop-down front hinged at the bottom, held level by folding stays on both sides"
  : front.lift === "HL" ? "horizontal lift-up front that rises vertically, parallel to the cabinet, on Aventos HL lift arms"
    : front.lift === "HS" ? "horizontal up-and-over front that swings up and comes to rest above the cabinet top (Aventos HS)"
      : front.lift === "HF" ? "horizontal bi-fold lift-up front of two parts that fold upwards towards each other (Aventos HF)"
        : "horizontal lift-up front hinged at the top that swings upwards and stays open at any angle (Aventos HK top)";

export const flapShortName = (front: FlapFront) => front.direction === "up"
  ? `вверх, ${liftSpec(front.lift!).title}`
  : "вниз, откидной";

// ---------------------------------------------------------------------------
// Ячейка стеллажа

export const cellFrontOpening = (cell: Pick<ShelvingCellConfig, "doorOpening" | "doorHinge">): CellFrontOpening =>
  cell.doorOpening ?? (cell.doorHinge === "right" ? "right" : "left");

export interface CellFrontLeaf {
  /** Смещение от левого края фасадного прямоугольника, мм. */
  leftMm: number;
  widthMm: number;
  heightMm: number;
  kind: "hinged" | "flap";
  /** У распашной — сторона петель: -1 слева, 1 справа. */
  hingeSide?: -1 | 1;
}

/**
 * Створки фасада ячейки по её прямоугольнику: одна или две распашные либо
 * один откидной фасад (у складного HF — из двух частей по высоте).
 */
export const cellFrontLeaves = (
  cell: Pick<ShelvingCellConfig, "doorOpening" | "doorHinge" | "doorLift">,
  widthMm: number,
  heightMm: number,
  /** Свободно над фасадом, мм: 0 — сверху другая ячейка (shelvingCellHeadroomMm). */
  headroomMm = Infinity,
  /** Подъёмники, которые у изделия не ставят (shelvingExcludedLifts). */
  excluded: readonly LiftType[] = []
): { opening: CellFrontOpening; leaves: CellFrontLeaf[]; flap: FlapFront | null } => {
  const opening = cellFrontOpening(cell);
  if (opening === "double") {
    const leafWidth = (widthMm - FRONT_SPLIT_GAP_MM) / 2;
    return {
      opening,
      flap: null,
      leaves: [
        { leftMm: 0, widthMm: leafWidth, heightMm, kind: "hinged", hingeSide: -1 },
        { leftMm: leafWidth + FRONT_SPLIT_GAP_MM, widthMm: leafWidth, heightMm, kind: "hinged", hingeSide: 1 }
      ]
    };
  }
  if (opening === "up" || opening === "down") {
    const flap = flapFront(opening, cell.doorLift, heightMm, headroomMm, excluded);
    if (flap) return { opening, flap, leaves: [{ leftMm: 0, widthMm, heightMm, kind: "flap" }] };
    // Механизм не подходит по высоте (ячейку изменили) — распашная, пока не выберут другое.
    return { opening: "left", flap: null, leaves: [{ leftMm: 0, widthMm, heightMm, kind: "hinged", hingeSide: -1 }] };
  }
  return { opening, flap: null, leaves: [{ leftMm: 0, widthMm, heightMm, kind: "hinged", hingeSide: opening === "right" ? 1 : -1 }] };
};

/** Подпись в техкарте и PDF. */
export const cellFrontOpeningLabel = (opening: CellFrontOpening, flap: FlapFront | null) =>
  opening === "double" ? "две дверцы, петли по краям"
    : flap ? flapShortName(flap)
      : opening === "right" ? "петли справа" : "петли слева";

// ---------------------------------------------------------------------------
// Антресоль шкафа: в каждом пролёте — один откидной фасад вместо дверей.

export const mezzanineFlapDirection = (module: Pick<WardrobeModule, "type" | "door" | "doorOpening">): FlapDirection | null =>
  module.type === "mezzanine" && module.door === "hinged" && (module.doorOpening === "up" || module.doorOpening === "down")
    ? module.doorOpening
    : null;

/** Комод (15.10): подъёмник HS у комодов не ставим — фасад ложился бы на столешницу. */
export const CHEST_EXCLUDED_LIFTS: readonly LiftType[] = ["HS"];
export const shelvingExcludedLifts = (module: Pick<WardrobeModule, "type" | "shelvingKind">): readonly LiftType[] =>
  module.type === "shelving" && module.shelvingKind === "chest" ? CHEST_EXCLUDED_LIFTS : [];
